import { useEffect, useState } from "react";
import { Copy, Eye, EyeOff } from "lucide-react";
import { Button } from "../ui/button";
import { fetchServerCredentials } from "../../hooks/useCredentials";
import { errorMessage } from "../../hooks/useServers";

const AUTO_HIDE_MS = 30000; // la contraseña vuelve a ocultarse sola

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// «Ver credenciales»: ficha completa de un servidor. Solo la ve quien tiene sesión; la IA nunca pasa por aquí.
export function CredentialsCard({ projectId, serverId, onHide }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchServerCredentials(projectId, serverId)
      .then((d) => !cancelled && setData(d))
      .catch((err) => !cancelled && setError(errorMessage(err, "No se pudieron cargar las credenciales.")));
    return () => {
      cancelled = true;
    };
  }, [projectId, serverId]);

  useEffect(() => {
    if (!showSecret) return undefined;
    const t = setTimeout(() => setShowSecret(false), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [showSecret]);

  const copy = async (label, value) => {
    if (await copyText(value)) {
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    }
  };

  if (error) return <p data-testid="credentials-card-error" className="text-xs text-red-500">{error}</p>;
  if (!data) return <p className="text-xs text-muted-foreground">Cargando...</p>;

  const isKey = data.auth_method === "key";
  const rows = [
    ["Nombre", data.name],
    ["Tipo", data.type === "routeros" ? "MikroTik RouterOS (API)" : data.type === "olt" ? "OLT Huawei (Telnet)" : "Servidor Linux (SSH)"],
    ["IP / host", data.host],
    ["Puerto", String(data.port)],
    ["Usuario", data.username],
    ["Acceso", data.access === "read_only" ? "Solo lectura" : "Lectura + cambios con confirmación"],
  ];
  if (data.via) rows.push(["Vía", data.via]);

  return (
    <div data-testid="credentials-card" className="rounded-md border border-border bg-muted/30 p-3 space-y-1.5 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-2">
          <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
          <span className="font-mono break-all" data-testid={`cred-${label}`}>{value}</span>
        </div>
      ))}
      <div className="flex gap-2 items-start">
        <span className="w-20 shrink-0 text-muted-foreground">{isKey ? "Llave" : "Contraseña"}</span>
        <span className="font-mono break-all whitespace-pre-wrap min-w-0" data-testid="cred-secret">
          {showSecret ? data.secret : "••••••••••"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" data-testid="cred-toggle" onClick={() => setShowSecret((v) => !v)}>
          {showSecret ? <EyeOff className="h-3.5 w-3.5 mr-1.5" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
          {showSecret ? "Ocultar" : "Mostrar"} {isKey ? "llave" : "contraseña"}
        </Button>
        <Button type="button" size="sm" variant="outline" data-testid="cred-copy-secret" onClick={() => copy("secret", data.secret)}>
          <Copy className="h-3.5 w-3.5 mr-1.5" /> {copied === "secret" ? "Copiada" : "Copiar contraseña"}
        </Button>
        <Button type="button" size="sm" variant="outline" data-testid="cred-copy-address" onClick={() => copy("addr", `${data.username}@${data.host}:${data.port}`)}>
          <Copy className="h-3.5 w-3.5 mr-1.5" /> {copied === "addr" ? "Copiado" : "Copiar usuario@IP"}
        </Button>
        <Button type="button" size="sm" variant="ghost" data-testid="cred-hide-card" onClick={onHide}>
          Cerrar ficha
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Queda anotado en el registro que se mostró esta ficha. La contraseña se oculta sola a los 30 s.</p>
    </div>
  );
}
